// @ts-nocheck
/* eslint-disable @typescript-eslint/strict-boolean-expressions */

import * as arrow from 'apache-arrow/Arrow.dom';
import Plotly from 'plotly.js';
import {
  getCurrentDate,
  getDateFormatted,
  isLatestTradingDay,
  ONE_HOUR_MS,
  ONE_MIN_MS,
  predicateSearch,
  predicateSearchWithFlags,
  roundToNearest,
  roundUpToNearest,
  stockMarketClose,
  stockMarketOpen,
} from './shared';
import { ET, dayjs } from 'util/shared/date';
import {
  TraceLense,
  TraceStrikeBarType,
  OIScaleRange,
  PRICE_BOUNDS,
  PriceCandle,
  PriceCandleWithDateTime,
  StrikeBarsDailyTracker,
  StrikeBarsDailyTrackerEntry,
  TraceContourData,
  TraceGreek,
  ZoomData,
  TimestampMillis,
} from '../types';
import { IntradayStrikeGEX, IntradayStats, OIEntityValue } from 'types/oi';
import {
  DEFAULT_MAX_AGE_MS,
  DEFAULT_MAX_AGE_PADDING_MS,
  HEATMAP_TRACE_NAME,
  HIRO_TRACE_NAME,
  TraceFiltersAxisLabels,
  TraceFiltersStatsInnerKeys,
  TraceFiltersToParamKeys,
  LAST_TRACE_UPDATE_HOUR_ET,
  PRICE_CANDLES_TRACE_NAME,
} from '../config/oi';
import { HeatmapColorSettings } from '../theme';
import { isFinite } from 'lodash';
import { readParquet } from 'parquet-wasm';
import { CandlestickSeries } from 'lightweight-charts';
import { IntradayBarType } from 'gen/stream-api/requests/types.gen';

const HIRO_Y_AXIS_BASE_PADDING = 250_000_000;

const LENSE_BASE_COLORSCALE_RANGES = new Map([
  [TraceLense.GAMMA, 20_000_000_000], // 20B
  [TraceLense.DELTA, 50_000_000_000], // 50B
  [TraceLense.GAMMA_COLOR, 10_000_000], // 10M
  [TraceLense.DELTA_CHARM_DIRECTIONAL, 10_000_000], // 10M
  [TraceLense.DELTA_END_DIFF, 5_000_000_000], // 5B
  [TraceLense.DELTA_DIRECTIONAL, 5_000_000_000], // 5B
  [TraceLense.GAMMA_DIRECTIONAL, 10_000_000_000], // 10B
]);

// V1 doesn't use bank method and has smaller values
const LENSE_BASE_COLORSCALE_RANGES_DEPRECATED = new Map([
  ...LENSE_BASE_COLORSCALE_RANGES.entries(),
  [TraceLense.GAMMA, 500_000_000], // 500M
  [TraceLense.GAMMA_DIRECTIONAL, 100_000_000], // 100M
]);

export type TraceStrikeBarsMapStore = {
  colors: string[];
  x: number[];
  y: number[];
};

export function getStrikeBarsData(
  table: arrow.Table<IntradayStrikeGEX>,
  invert: boolean,
  netParquetKeys: OIEntityValue[],
  timestamp: dayjs.Dayjs | null,
  zeroDTE: boolean,
  strikeBarType: TraceStrikeBarType,
  heatmapColorSettings: HeatmapColorSettings,
  startTs: number,
  endTs: number,
): {
  latestTimestamp?: number;
  allDataByTs?: Map<number, Map<string, TraceStrikeBarsMapStore>>;
} {
  if (strikeBarType === TraceStrikeBarType.NONE) {
    return {};
  }

  const array = table.toArray() as IntradayStrikeGEX[];
  if (array.length === 0 || timestamp == null) {
    return {};
  }

  const negGammaColor = heatmapColorSettings.negStrikeColor;
  const posGammaColor = heatmapColorSettings.posStrikeColor;

  const allDataByTs: Map<
    number,
    Map<string, TraceStrikeBarsMapStore>
  > = new Map();

  const start =
    startTs === 0
      ? 0
      : predicateSearch(array, (e) => e.timestamp < startTs) + 1;
  const end = predicateSearch(array, (e) => e.timestamp <= endTs);
  const data = array.slice(start, Math.max(0, end) + 1);

  for (const e of data) {
    const strike = e.strike_price;
    const values = getGexValues(
      e,
      netParquetKeys,
      invert,
      zeroDTE,
      strikeBarType,
      heatmapColorSettings,
    );
    for (const entry of values) {
      const mapToUse =
        allDataByTs.get(e.timestamp) ??
        new Map<string, TraceStrikeBarsMapStore>();
      const curr = mapToUse.get(entry.name) ?? {
        colors: [],
        x: [],
        y: [],
      };
      curr.x.push(entry.value);
      curr.y.push(strike);
      curr.colors.push(
        entry.color ?? (entry.value >= 0 ? posGammaColor : negGammaColor),
      );
      allDataByTs.set(e.timestamp, mapToUse.set(entry.name, curr));
    }
  }

  const latestTimestamp = array[array.length - 1].timestamp;

  return { latestTimestamp, allDataByTs };
}

const getGexValues = (
  data: IntradayStrikeGEX,
  netParquetKeys: OIEntityValue[],
  invert: boolean,
  zeroDTE: boolean,
  strikeBarType: TraceStrikeBarType,
  heatmapColorSettings: HeatmapColorSettings,
): { name: string; value: number; color?: string }[] => {
  switch (strikeBarType) {
    case TraceStrikeBarType.GAMMA:
      return [
        {
          name: strikeBarTraceName(strikeBarType),
          value: getTableValue(
            data,
            netParquetKeys,
            greekForLense(TraceLense.GAMMA),
            invert,
            zeroDTE,
          ),
        },
      ];
    case TraceStrikeBarType.OI:
    case TraceStrikeBarType.OI_NET: {
      const buy = getTableValue(
        data,
        netParquetKeys,
        invert ? 'sell_oi' : 'buy_oi',
        false,
        zeroDTE,
      );
      const sell = getTableValue(
        data,
        netParquetKeys,
        invert ? 'buy_oi' : 'sell_oi',
        false,
        zeroDTE,
      );
      const color = data.is_call
        ? heatmapColorSettings.callColor
        : heatmapColorSettings.putColor;
      if (strikeBarType === TraceStrikeBarType.OI_NET) {
        return [
          {
            name: strikeBarTraceName(strikeBarType, data.is_call),
            value: buy - sell,
            color,
          },
        ];
      }

      return [
        {
          name: strikeBarTraceName(strikeBarType, data.is_call, true),
          value: buy,
          color,
        },
        {
          name: strikeBarTraceName(strikeBarType, data.is_call, false),
          value: -sell,
          color,
        }, // in non-net view, sell should be negative
      ];
    }
    case TraceStrikeBarType.NONE:
      return [];
  }
};

export const strikeBarTraceName = (
  strikeBarType: TraceStrikeBarType,
  isCall?: boolean,
  long?: boolean,
) => {
  if (strikeBarType === TraceStrikeBarType.GAMMA) {
    return 'GEX';
  }

  const type = isCall ? 'Call' : 'Put';
  if (strikeBarType === TraceStrikeBarType.OI_NET) {
    return `Net ${type} Position`;
  }

  return `${long ? 'Long' : 'Short'} ${type}s`;
};

export const strikeBarTraceNameYOffset = (traceName: string) => {
  // different traces have different offsets here because of their different relative
  // y position to the y axis strike coordinate. i.e. 5500 Long Puts and 5500 Long Calls are
  // offset at different amounts relative to the 5500 y coordinate
  switch (traceName) {
    case strikeBarTraceName(TraceStrikeBarType.OI, true, true):
    case strikeBarTraceName(TraceStrikeBarType.OI_NET, true):
      return -0.5;
    case strikeBarTraceName(TraceStrikeBarType.OI, true, false):
      return -1;
    case strikeBarTraceName(TraceStrikeBarType.OI, false, false):
    case strikeBarTraceName(TraceStrikeBarType.OI_NET, false):
      return 0.5;
    case strikeBarTraceName(TraceStrikeBarType.OI, false, true):
      return 1;
    default:
      return 0;
  }
};

export const maxStrikeBarY3Offset = (type: TraceStrikeBarType) => {
  return Math.abs(
    strikeBarTraceNameYOffset(strikeBarTraceName(type, false, false)),
  );
};

const getTableValue = (
  e: IntradayStrikeGEX,
  netParquetKeys: OIEntityValue[],
  suffix: string,
  invert: boolean,
  zeroDTE?: boolean,
): number => {
  const value: number = netParquetKeys.reduce(
    (tot: number, k) =>
      tot + (e[tableKeyForLense(k, suffix, zeroDTE)] as number),
    0,
  );
  return invert ? -value : value;
};

const getTableV2Value = (
  e: IntradayStrikeGEX,
  netParquetKeys: OIEntityValue[],
  invert: boolean,
): number => {
  const value: number = netParquetKeys.reduce(
    (accum: number, k: string) => accum + (e[k] as number),
    0,
  );
  return invert ? -value : value;
};

export interface ContourDataCommonRowType {
  time: number;
  timestamp: Date;
  spot: number;
}

export function getContourData<T extends ContourDataCommonRowType>(
  table: arrow.Table<T>,
  timestamp: TimestampMillis | undefined,
  parquetKeys: OIEntityValue[],
  invert: boolean = false,
  priceBounds: [number, number] | null,
  offsetMs: TimestampMillis,
  filter: TraceLense,
  priceCandles: PriceCandleWithDateTime[],
  chartWidth: number,
  userTraceV2Enabled: boolean = false,
): TraceContourData | null {
  const array = table.toArray() as T[];
  if ((array?.length ?? 0) === 0) {
    return null;
  }

  // eslint-disable-next-line prefer-const
  let { x, y, z, rawChartTimes } = parseContourArray(
    array,
    timestamp,
    parquetKeys,
    invert,
    priceBounds,
    offsetMs,
    filter,
    userTraceV2Enabled,
  );

  if (z.length > 0) {
    if (
      filter === TraceLense.DELTA_DIRECTIONAL ||
      filter === TraceLense.GAMMA_DIRECTIONAL ||
      filter === TraceLense.DELTA_END_DIFF
    ) {
      if (userTraceV2Enabled) {
        const times = [...new Set(rawChartTimes)].sort((a, b) => a - b);
        let beg = 0;
        for (const tgt of times) {
          const endIdx = predicateSearch(rawChartTimes, (t) => t <= tgt);
          const priceIdx = predicateSearch(
            priceCandles ?? [],
            (c) => c.time <= Math.min(tgt, timestamp),
          );
          const price =
            priceCandles?.[priceIdx] ??
            priceCandles?.[(priceCandles?.length ?? 0) - 1];
          if (price != null) {
            // TODO: This doesn't work for DELTA_END_DIFF (which is not shipped)
            const greekValue = getGreekValueAtPrice(
              y,
              z,
              rawChartTimes,
              price.close,
              tgt,
              beg,
              endIdx + 1,
            );
            const invertGreek = greekForLense(filter) === TraceGreek.Delta;
            applyDirectionalFilter(z, greekValue, invertGreek, beg, endIdx + 1);
            beg = endIdx + 1;
          }
        }
      } else {
        // TODO: delete this block
        const idx = predicateSearch(priceCandles, (c) => c.time <= timestamp);
        const lastPrice = priceCandles?.[idx];
        if (lastPrice != null) {
          const lastPriceGreekValue = getGreekValueAtLastPrice(
            y,
            z,
            rawChartTimes,
            lastPrice,
            filter,
          );
          applyDirectionalFilter(
            z,
            lastPriceGreekValue,
            greekForLense(filter) === TraceGreek.Delta,
          );
        }
      }
    }
  }

  const type = 'contour';

  return {
    chartData: {
      x,
      y,
      z,
      type,
      colorbar: shouldShowAxisLabels(chartWidth)
        ? {
            title: TraceFiltersAxisLabels.get(filter)!,
            titleside: 'right',
          }
        : {},
      hoverinfo: 'none',
      name: HEATMAP_TRACE_NAME,
    },
    firstChartTimestamp: rawChartTimes[0],
    lastChartTimestamp: rawChartTimes[rawChartTimes.length - 1],
    latestTimestamp: array[array.length - 1].timestamp,
  };
}

interface ParseContourArrayResult<T extends ContourDataCommonRowType> {
  /// parquet rows, filtered down to a time range and then further filtered to
  /// fit into the the pricing reticle
  data: T[];

  ///////////////////////////////////////////////////////////////////
  // values extracted and synthesized from the filtered `data` field:
  ///////////////////////////////////////////////////////////////////

  /// the extracted timestamp, converted to a Date an with a weird offset to
  /// display the eastern timezone while from the user timezone...
  x: Date[];

  /// the extracted spot price
  y: number[];

  /// the actual heatmap values, synthesized from other column in the selected data
  z: number[];

  rawChartTimes: TimestampMillis[];
}
export const parseContourArray = <T extends ContourDataCommonRowType>(
  array: T[],

  // selects the view of the world at this time, projecting into the future, for any
  // projection beyond this time.  For projections  _prior_ to this timestamp,
  // use the view of the world at or nearest to _just_ before those times.
  timestamp: TimestampMillis,
  netParquetKeys: OIEntityValue[],
  invert: boolean = false,
  priceBounds: [number, number] | null,
  offsetMs: TimestampMillis,
  filter: TraceLense,
  userTraceV2Enabled: boolean = false,
): ParseContourArrayResult<T> => {
  const rawChartTimes: TimestampMillis[] = [];
  const x: Date[] = [];
  const y: number[] = [];
  const z: number[] = [];
  const data: T[] = [];

  if (timestamp == null) {
    timestamp = array[array.length - 1].timestamp;
  }
  const timeEnd: number = array[array.length - 1]?.time;

  const traceDate = dayjs(Math.max(array[array.length - 1].timestamp, timeEnd));
  const mktOpen = dayjs(timeEnd).tz(ET).hour(9).minute(30);
  const chartStart =
    timestamp > mktOpen.hour(2).valueOf()
      ? dayjs.min(mktOpen, dayjs(timestamp).subtract(2, 'hours')).valueOf()
      : timestamp;
  let tgt = Math.max(array[0].time, array[0].timestamp, chartStart);
  const chartTimes = [
    ...array.reduce((times, e) => times.add(e.time), new Set()),
  ].sort((a, b) => a - b);
  tgt = chartTimes[predicateSearch(chartTimes, (t) => t < tgt) + 1];

  const marketOpenTs = stockMarketOpen(traceDate).valueOf();
  const marketCloseTs = stockMarketClose(traceDate).valueOf();

  // First create our "timestamp" bounds (tsStartIdx and tsEndIdx).  Below we will
  // search for a chart "time" matching our `tgt` within these bounds
  let tsEndIdx = predicateSearch(array, (e) => e.timestamp <= tgt);
  const tsEnd = array[tsEndIdx]?.timestamp;
  let tsStartIdx =
    predicateSearch(array, (e) => e.timestamp < tsEnd, 0, tsEndIdx) + 1;
  if (tsStartIdx >= array.length && array[0]?.timestamp < tgt) {
    tsStartIdx = 0;
  }

  let tgtSearchWatchdog = 1_000;
  while (tgt != null && tgt <= timeEnd) {
    if (--tgtSearchWatchdog <= 0) {
      console.error('Iterated on `tgt` too many times and watchdog fired.');
      break;
    }
    // If we haven't already reached the end of our timestamp range, and we are
    // searching for a time that can be represented by a timestamp larger than
    // the current frame, find our next bounds in the array for the next
    // timestamp. Only advance if we're less than `timestamp`
    const nextTs = array[tsEndIdx + 1]?.timestamp;
    if (tsEndIdx < array.length - 1 && tgt >= nextTs && nextTs < timestamp) {
      const nextTsStart =
        predicateSearch(array, (e) => e.timestamp < tgt, tsStartIdx) + 1;
      if (nextTsStart < array.length) {
        tsStartIdx = nextTsStart;
        // We have 11:00, 11:10, & 11:20 timetamps.  We're searching for
        // 11:15. We want to grab the 11:10 timestamp.  Our binary search will find
        // 11:20.  When that happens, manually adjust to get the 11:10 range
        if (array[tsStartIdx]?.timestamp > tgt) {
          tsStartIdx = tsEndIdx + 1;
        }
        tsEndIdx = predicateSearch(
          array,
          (e) => e.timestamp <= tgt,
          tsStartIdx,
        );
      }
    }

    // Filter down to the time in question. Using binary search cuts filter time significantly

    // eslint-disable-next-line prefer-const
    let { foundIdx: start, found: _ } = predicateSearchWithFlags(
      array,
      (e) => e.time < tgt,
      tsStartIdx,
      tsEndIdx,
    );
    start += 1;

    // We don't expect to find an element with `time < tgt` always.
    // We're really searching for the first element that is equal to our target
    const startFound = array[start].time === tgt;

    const { foundIdx: end, found: endFound } = predicateSearchWithFlags(
      array,
      (e) => e.time <= tgt,
      start,
      tsEndIdx + 1,
    );

    let nextSpotTimeInTsStartIdx: number | null = null;
    let nextSpotTimeInTsEndIdx: number | null = null;
    let nextSpotTimeInTsSpotIdx: number | null = null;
    let nextSpotTime: number | null = null;
    if (
      startFound &&
      endFound &&
      filter === TraceLense.DELTA_CHARM_DIRECTIONAL
    ) {
      nextSpotTime = array[end + 1]?.time;
      if (nextSpotTime != null) {
        nextSpotTimeInTsStartIdx = end + 1;
        const { foundIdx: tsEndFoundIdx, found: tsEndFound } =
          predicateSearchWithFlags(
            array,
            (e) => e.time <= nextSpotTime,
            nextSpotTimeInTsStartIdx,
            tsEndIdx + 1,
          );
        if (tsEndFound) {
          nextSpotTimeInTsEndIdx = tsEndFoundIdx;
          nextSpotTimeInTsSpotIdx = nextSpotTimeInTsStartIdx;
        }
      }
    }
    for (let i = start; startFound && endFound && i <= end; ++i) {
      const e = array[i];
      if (
        priceBounds != null &&
        (e.spot < priceBounds[0] || e.spot > priceBounds[1])
      ) {
        continue;
      }

      let value: number | null = null;

      const currentSpotTimeSpotValue: number | null = userTraceV2Enabled
        ? getTableV2Value(e, netParquetKeys, invert)
        : getTableValue(e, netParquetKeys, greekForLense(filter), invert);

      if (filter === TraceLense.DELTA_CHARM_DIRECTIONAL) {
        if (nextSpotTime === marketOpenTs || nextSpotTime === marketCloseTs) {
          value = 0;
        } else if (
          nextSpotTimeInTsStartIdx != null &&
          nextSpotTimeInTsEndIdx != null &&
          nextSpotTimeInTsSpotIdx != null
        ) {
          for (
            ;
            nextSpotTimeInTsSpotIdx < nextSpotTimeInTsEndIdx &&
            array[nextSpotTimeInTsSpotIdx]?.spot <= e.spot;
            nextSpotTimeInTsSpotIdx++
          ) {
            const nextSpotTimeRow = array[nextSpotTimeInTsSpotIdx];
            if (nextSpotTimeRow.spot === e.spot) {
              const nextSpotTimeSpotValue: number | null = userTraceV2Enabled
                ? getTableV2Value(nextSpotTimeRow, netParquetKeys, invert)
                : getTableValue(
                    nextSpotTimeRow,
                    netParquetKeys,
                    greekForLense(filter),
                    invert,
                  );
              // When calculating charm pressure, we need to invert the value.
              // It shows the change in delta to show change in buying pressure,
              // it needs to be inverted
              value =
                -(nextSpotTimeSpotValue - currentSpotTimeSpotValue) /
                ((nextSpotTimeRow.time - e.time) / 60_000);
            }
          }
        }
      } else {
        value = currentSpotTimeSpotValue;
      }

      if (value != null) {
        x.push(new Date(e.time + offsetMs));
        y.push(Number(e.spot));
        z.push(value);
        rawChartTimes.push(e.time);
        data.push(e);
      }
    }

    if (endFound && array[end + 1]?.timestamp > array[end]?.timestamp) {
      // The next element's time will not necessarily be the next time. We have to search for it.
      tsStartIdx = end + 1;
      let timeIdx;
      let timeWatchdog = 100;
      do {
        tsEndIdx = predicateSearch(
          array,
          (e) => e.timestamp <= array[tsStartIdx].timestamp,
          tsStartIdx,
        );
        timeIdx = predicateSearch(
          array,
          (e) => e.time <= tgt,
          tsStartIdx,
          tsEndIdx + 1,
        );
        if (
          array[timeIdx + 1]?.time == null ||
          array[timeIdx + 1].time >= timeEnd
        ) {
          // We've exhausted all times that we render
          break;
        } else if (array[timeIdx + 1].time <= tgt) {
          tsStartIdx = tsEndIdx + 1;
        }
      } while (array[timeIdx + 1]?.time <= tgt && --timeWatchdog > 0);
      if (timeWatchdog <= 0) {
        console.error(
          'Iterated on `array[timeIdx + 1]?.time` too many times and watchdog fired.',
        );
        break;
      }
      tgt = array[timeIdx + 1]?.time;
    } else {
      // We can rely on time being the next element since we're in the same "timestamp block"
      tgt = array[end + 1]?.time;
    }
  }

  return { x, y, z, rawChartTimes, data };
};

export const getGreekValueAtLastPrice = (
  y: number[],
  z: number[],
  rawChartTimes: number[],
  lastPrice: PriceCandleWithDateTime,
  filter: TraceLense,
) => {
  let lastTime = lastPrice.datetime.valueOf();
  if (filter === TraceLense.DELTA_END_DIFF) {
    const lastChartTime = rawChartTimes[rawChartTimes.length - 1];
    const marketClose = stockMarketClose(dayjs(lastChartTime)).valueOf();
    const times = [...new Set(rawChartTimes)];
    const idx = predicateSearch(times, (t) => t < marketClose);
    lastTime = times[idx]; // last time before market close
  }

  return getGreekValueAtPrice(
    y,
    z,
    rawChartTimes,
    lastPrice?.close,
    lastTime,
    0,
    y.length,
  );
};

export const getContourColorOptions = <T extends ContourDataCommonRowType>(
  z: T,
  heatmapColorSettings: HeatmapColorSettings,
  filter: TraceLense,
  selectedScaleRange: OIScaleRange,
  userTraceV2Enabled: bool,
) => {
  const negativeTrendColor = heatmapColorSettings.negHeatmapColor;
  const positiveTrendColor = heatmapColorSettings.posHeatmapColor;
  const zeroColor = heatmapColorSettings.chartZeroColor;

  const colorscale = [
    [0, negativeTrendColor],
    [0.5, zeroColor],
    [1, positiveTrendColor],
  ];

  const contours = {
    coloring: 'heatmap',
    ...predefinedContourColorRange(
      selectedScaleRange,
      filter,
      z,
      userTraceV2Enabled,
    ),
  };

  return { contours, colorscale };
};

const predefinedContourColorRange = (
  selectedScaleRange: OIScaleRange,
  selectedLense: TraceLense,
  values: number[],
  userTraceV2Enabled: bool,
): { start: number; end: number } => {
  if (selectedScaleRange === OIScaleRange.AUTO) {
    const max = values.reduce((a, b) => Math.max(Math.abs(a), Math.abs(b)), 0);
    return { start: -max, end: max };
  }

  const rangeMinMax =
    (userTraceV2Enabled
      ? LENSE_BASE_COLORSCALE_RANGES.get(selectedLense)
      : LENSE_BASE_COLORSCALE_RANGES_DEPRECATED.get(selectedLense)) ??
    50_000_000;

  const multiplier = multiplierForScaleRange(selectedScaleRange);
  return {
    start: rangeMinMax * multiplier * -1,
    end: rangeMinMax * multiplier,
  };
};

const multiplierForScaleRange = (selectedScaleRange: OIScaleRange) => {
  if (selectedScaleRange === OIScaleRange.LOW) {
    return 0.5;
  } else if (selectedScaleRange === OIScaleRange.HIGH) {
    return 2;
  }

  return 1;
};

const getGreekValueAtPrice = (
  strikes: number[],
  values: number[],
  chartTimes: number[],
  lastPrice: number | undefined,
  timestamp: TimestampMillis | undefined,
  beg: number = 0,
  endExcl: number | undefined = null,
) => {
  if (lastPrice == null || timestamp == null) {
    return undefined;
  }
  const startIdx =
    predicateSearch(chartTimes, (t) => t < timestamp, beg, endExcl) + 1;
  const endIdx = predicateSearch(
    chartTimes,
    (t) => t <= chartTimes[startIdx],
    startIdx,
    endExcl,
  );

  for (let i = endIdx; i >= startIdx; --i) {
    if (lastPrice >= strikes[i]) {
      return values[i];
    } else if (strikes[i] >= lastPrice && strikes[i - 1] <= lastPrice) {
      // calculate gamma by finding the closest strikes and doing offset math if necessary to find out where the gamma
      // at the current price would lie in the range of the values of the 2 closest strikes
      const valueDiff = values[i] - values[i - 1];
      const strikeDiff = strikes[i] - strikes[i - 1];

      return (
        values[i - 1] + valueDiff * ((lastPrice - strikes[i - 1]) / strikeDiff)
      );
    }
  }

  return values[0];
};

/// Destructively alter z with new values based on last greek value passed in
const applyDirectionalFilter = (
  z: number[],
  lastPriceGreekValue: number | undefined,
  invert: boolean,
  begIncl: number = 0,
  endExcl: number | null = null,
) => {
  if (lastPriceGreekValue == null) {
    // dont throw here as this may trigger once without lastPrice set as the react state change bubbles through
    return;
  }
  endExcl = endExcl == null ? z.length : endExcl;
  for (let i = begIncl; i < endExcl; ++i) {
    const val = z[i];
    z[i] = (val - lastPriceGreekValue) * (invert ? -1 : 1);
  }
};

// Convert price candles into Plotly trace
export function getCandles(
  candles: PriceCandleWithDateTime[],
  timestamp: TimestampMillis | undefined,
  offsetMs: TimestampMillis,
  colorSettings: HeatmapColorSettings,
  minX: number | undefined,
  maxX: number | undefined,
): Plotly.Data {
  const xCutoff = timestamp ?? maxX ?? Infinity;
  const xStart = minX ?? 0;
  candles = candles.filter((c) => {
    const ts = c.datetime.valueOf();
    return ts <= xCutoff && ts >= xStart;
  });
  const x = candles.map((c) => new Date(c.datetime.valueOf() + offsetMs));
  const open = candles.map((c) => c.open);
  const high = candles.map((c) => c.high);
  const low = candles.map((c) => c.low);
  const close = candles.map((c) => c.close);
  return {
    x,
    open,
    high,
    low,
    close,
    xaxis: 'x',
    type: CandlestickSeries,
    hoverinfo: 'skip',
    name: PRICE_CANDLES_TRACE_NAME,
    increasing: {
      fillcolor: colorSettings.candleUpFillColor,
      line: { color: colorSettings.candleUpLineColor },
    },
    decreasing: {
      fillcolor: colorSettings.candleDownFillColor,
      line: { color: colorSettings.candleDownLineColor },
    },
    line: { width: 2 },
  };
}

export function getHiro(
  candles: PriceCandle[], // hiro does not use the 'useDayjs' useStreaming param yet, so it does not have datetime
  timestamp: TimestampMillis | undefined,
  offsetMs: TimestampMillis,
  color: string,
  symbol: string,
): {
  chartData: Plotly.Data;
  min: number;
  max: number;
} | null {
  const x = [];
  const y = [];
  let min = Infinity;
  let max = -1;

  candles = timestamp
    ? candles.filter((c) => c.time * 1_000 <= timestamp)
    : candles;

  if (candles.length === 0 || symbol.length === 0) {
    return null;
  }

  for (const candle of candles) {
    x.push(new Date(candle.time * 1_000 + offsetMs));
    y.push(candle.close);
    min = Math.min(candle.close, min);
    max = Math.max(candle.close, max);
  }

  return {
    chartData: {
      x,
      y,
      yaxis: 'y2',
      xaxis: 'x',
      type: 'scatter',
      line: { width: 2, color },
      mode: 'lines',
      name: HIRO_TRACE_NAME,
      hoverinfo: 'skip',
    },
    min,
    max,
  };
}

export const greekForLense = (lense: TraceLense): TraceGreek => {
  switch (lense) {
    case TraceLense.GAMMA:
    case TraceLense.GAMMA_DIRECTIONAL:
    case TraceLense.GAMMA_COLOR:
      return TraceGreek.Gamma;
    default:
      return TraceGreek.Delta;
  }
};

export function convertTwelveCandles(
  candles: PriceCandleWithDateTime[],
  candlesTz: string,
): PriceCandleWithDateTime[] {
  return candles?.map((c) => {
    const dt = dayjs.tz(c.datetime, candlesTz);
    return {
      ...c,
      datetime: dt,
      time: dt.unix(), // secs
      open: parseFloat(c.open),
      high: parseFloat(c.high),
      low: parseFloat(c.low),
      close: parseFloat(c.close),
    };
  });
}

export const tableKeyForLense = (
  key: string,
  suffix: string,
  zeroDTE?: boolean,
): keyof IntradayStrikeGEX => `${key}_${suffix}${zeroDTE ? '_0' : ''}`;

export const shouldShowAxisLabels = (chartWidth: number) => chartWidth >= 900;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getHiroRange = (hiroData: any, priceBounds: number) => {
  const priceBoundsMultiplier =
    PRICE_BOUNDS.length - PRICE_BOUNDS.indexOf(priceBounds);
  const minPadding = HIRO_Y_AXIS_BASE_PADDING * priceBoundsMultiplier;

  return [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    roundToNearest(hiroData.min - minPadding, HIRO_Y_AXIS_BASE_PADDING),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
    roundToNearest(hiroData.max + minPadding, HIRO_Y_AXIS_BASE_PADDING),
  ];
};

export const getStrikeBarsXAxisRange = (
  tracker: StrikeBarsDailyTracker | null,
  min: number | undefined,
  max: number | undefined,
  zoomBounds: number,
  useHistory: boolean,
) => {
  if (tracker == null || min == null || max == null) {
    return undefined;
  }

  let maxVal = 0;
  for (const trace of [...tracker.entriesPerTrace.keys()]) {
    const entry = tracker.entriesPerTrace.get(trace)!;
    for (const strike of [...entry.keys()]) {
      if (strike < min || strike > max) {
        continue;
      }

      const e = entry.get(strike)!;
      maxVal = Math.max(
        maxVal,
        Math.abs((useHistory ? e.dailyMax : e.last) ?? 0),
        Math.abs((useHistory ? e.dailyMin : e.last) ?? 0),
      );
    }
  }

  maxVal =
    roundUpToNearest(
      maxVal,
      tracker.type === TraceStrikeBarType.GAMMA ? 10_000_000 : 1_000,
    ) *
    (1 - zoomBounds);

  // When all values are zero (e.g. 0DTE after close), show a small default
  // range so the chart still renders a visible axis centered on zero.
  const fallback =
    tracker.type === TraceStrikeBarType.GAMMA ? 10_000_000 : 1_000;

  return [-(maxVal || fallback), maxVal || fallback];
};

export const getStatsPercentile = (
  value: number | undefined,
  lenseOrStrikeType: TraceLense | TraceStrikeBarType,
  statsData: IntradayStats,
  parquetKeys: string[],
  inverted: boolean,
  isStrikeBar: boolean,
  zeroDte: boolean,
  traceName: string,
  lookbackDays: number,
) => {
  const key = TraceFiltersToParamKeys.get(lenseOrStrikeType);
  if (
    value == null ||
    statsData == null ||
    key == null ||
    statsData[key] == null ||
    parquetKeys.length !== 1 ||
    lenseOrStrikeType === TraceStrikeBarType.NONE
  ) {
    return null;
  }

  const parquetKey = parquetKeys[0];
  const uninvertedVal = inverted ? -value : value;

  let innerKey = TraceFiltersStatsInnerKeys.get(lenseOrStrikeType) ?? key;
  if (lenseOrStrikeType === TraceStrikeBarType.OI) {
    // if the trace name equals what the tracename would be if long puts or calls, then we want to use the 'buy' key
    const buy = [
      strikeBarTraceName(TraceStrikeBarType.OI, true, true),
      strikeBarTraceName(TraceStrikeBarType.OI, false, true),
    ].includes(traceName);
    innerKey = buy ? 'buy' : 'sell';
  }

  const valKey = `${innerKey}${isStrikeBar && zeroDte ? '_0' : ''}${
    lenseOrStrikeType === TraceStrikeBarType.OI
      ? ''
      : uninvertedVal > 0
        ? '_pos'
        : '_neg'
  }`;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const statsForKey = statsData[key][`${lookbackDays}`]?.[parquetKey]?.[valKey];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const percentiles = statsForKey?.percentiles;
  if (percentiles == null) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
  const percentilesArr = [...Object.entries(percentiles)].map((v) => [
    parseInt(v[0]),
    v[1],
  ]) as [number, number][];
  const absVal = Math.abs(value);
  const idx = predicateSearch(
    percentilesArr,
    (percentile: [number, number]) => percentile[1] < absVal,
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const lower = idx < 0 ? [0, statsForKey.min] : percentilesArr[idx];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const upper =
    idx < percentilesArr.length - 1
      ? percentilesArr[idx + 1]
      : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        [100, statsForKey.max];

  const valDiffPerOne = (upper[1] - lower[1]) / (upper[0] - lower[0]);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const percent = (lower[0] + (absVal - lower[1]) / valDiffPerOne) as number;
  return percent.toFixed(2);
};

export const updateStrikeBarsTracker = (
  data: Map<number, Map<string, TraceStrikeBarsMapStore>> | undefined,
  currType: TraceStrikeBarType,
  strikeBarsTracker: StrikeBarsDailyTracker | null,
  zeroDte: boolean,
) => {
  if (data == null) {
    return strikeBarsTracker;
  }

  const entriesPerTrace: Map<
    string,
    Map<number, StrikeBarsDailyTrackerEntry>
  > = strikeBarsTracker?.entriesPerTrace ??
  new Map<string, Map<number, StrikeBarsDailyTrackerEntry>>();

  // since these are map keys, no guarantee they are sorted in the way we want
  // so we want to ensure we sort them high -> low so that we can set the 10,30,60 min updates based on
  // the first timestamp.
  const timestampsSorted = [...data.keys()].sort((a, b) => b - a);
  const latestTimestamp = timestampsSorted[0];
  const threshold = stockMarketClose(dayjs(latestTimestamp))
    .subtract(1, 'day')
    .valueOf();
  for (const ts of timestampsSorted) {
    if (ts < threshold) {
      break;
    }
    const map = data.get(ts)!;
    // for GEX there is just one trace
    // for OI we have traces for Long/Short Call/Put
    // for net OI it's Net Call/Put
    // see getGexValues()
    const traces = [...map.keys()];
    for (const traceName of traces) {
      // for each trace, set the 10, 30, and 60 min value, and the daily min/max, using the prev tracker (if available)
      // then save that data on the new tracker map, corresponding to the strike that we're saving data for
      const entriesPerStrike =
        entriesPerTrace.get(traceName) ??
        new Map<number, StrikeBarsDailyTrackerEntry>();
      const setThirty = ts === latestTimestamp - 30 * ONE_MIN_MS;
      const setSixty = ts === latestTimestamp - ONE_HOUR_MS;
      const setLast = ts === latestTimestamp - 10 * ONE_MIN_MS;

      const valuesForTrace = map.get(traceName)!;
      const strikes = valuesForTrace.y;

      for (let j = 0; j < strikes.length; j++) {
        const strike = strikes[j];
        const val = valuesForTrace.x[j];
        const entry = entriesPerStrike.get(strike) ?? {};

        entry.dailyMax = Math.max(
          isFinite(entry.dailyMax) ? entry.dailyMax : -Infinity,
          val,
        );
        entry.dailyMin = Math.min(
          isFinite(entry.dailyMin) ? entry.dailyMin : Infinity,
          val,
        );

        if (setThirty) {
          entry.thirtyMin = val;
        }
        if (setSixty) {
          entry.hour = val;
        }
        if (setLast) {
          entry.last = val;
        }

        entriesPerStrike.set(strike, entry);
      }

      entriesPerTrace.set(traceName, entriesPerStrike);
    }
  }

  return {
    type: currType,
    latestTimestamp,
    entriesPerTrace,
    zeroDte,
  };
};

export const pollAtFromHeaders = (headers: Headers) =>
  dayjs().valueOf() + maxAgeMsFromHeaders(headers);

const maxAgeMsFromHeaders = (headers?: Headers) => {
  if (headers == null) {
    return DEFAULT_MAX_AGE_MS + DEFAULT_MAX_AGE_PADDING_MS;
  }

  const maxAgeStr = (
    headers.get ? headers.get('cache-control') : headers['cache-control']
  ) as string;
  const maxAgeNumStr = /max-age=(\d+)/.exec(maxAgeStr)?.[1];
  const newMaxAge = maxAgeNumStr == null ? NaN : parseFloat(maxAgeNumStr);
  return (
    (isNaN(newMaxAge) ? DEFAULT_MAX_AGE_MS : newMaxAge * 1_000) +
    DEFAULT_MAX_AGE_PADDING_MS
  );
};

export const responseToTable = async (resp: Response) => {
  const buffer = await resp.arrayBuffer();
  const arrowTable = readParquet(new Uint8Array(buffer));
  return arrow.tableFromIPC(arrowTable.intoIPCStream());
};

export const getParquetUrl = (
  action: string,
  intradayDate: dayjs.Dayjs,
  intradaySym: string,
) => {
  const sym = encodeURIComponent(intradaySym);
  const params = new URLSearchParams({ sym });
  if (intradayDate != null) {
    params.append('date', getDateFormatted(intradayDate));
  }
  return `v1/oi/${action}?${params.toString()}`;
};

// TODO: Delete when on v2
export const strikeBarParquetUrlForType = (
  strikeType: TraceStrikeBarType,
  intradayDate: dayjs.Dayjs,
  intradaySym: string,
): string | null => {
  return strikeType === TraceStrikeBarType.NONE
    ? null
    : getParquetUrl(
        [TraceStrikeBarType.OI, TraceStrikeBarType.OI_NET].includes(strikeType)
          ? 'intradayStrikeOI'
          : 'intradayStrikeGEX',
        intradayDate,
        intradaySym,
      );
};

export const getApiBarType = (
  barType: TraceStrikeBarType,
): IntradayBarType | null => {
  switch (barType) {
    case TraceStrikeBarType.GAMMA:
      return 'gamma';
    case TraceStrikeBarType.OI:
    case TraceStrikeBarType.OI_NET:
      return 'oi';
    default:
      return null;
  }
};

export const getTableParquetUrl = (
  greek: TraceGreek,
  intradayDate: dayjs.Dayjs,
  intradaySym: string,
) => {
  const action = greek === TraceGreek.Delta ? 'intradayDelta' : 'intradayGamma';
  return getParquetUrl(action, intradayDate, intradaySym);
};

export const getYRange = (
  zoomData: ZoomData | undefined,
  min: number | undefined,
  max: number | undefined,
  strikeBarType: TraceStrikeBarType,
): undefined | [number, number] => {
  // need some Y-padding so the bars do not get cutoff
  let padding = 0;
  if (strikeBarType === TraceStrikeBarType.OI_NET) {
    padding = 0.5;
  } else if (strikeBarType === TraceStrikeBarType.OI) {
    padding = 1;
  }

  let yRange: number[] | undefined = zoomData?.y;
  yRange ||= min == null || max == null ? undefined : [min, max];
  if ((yRange?.length ?? 0) === 0) {
    yRange = undefined;
  } else {
    yRange = [
      yRange![0] - maxStrikeBarY3Offset(strikeBarType) - padding,
      yRange![1] + maxStrikeBarY3Offset(strikeBarType) + padding,
    ];
  }

  return yRange;
};

export const isPollingOpen = () =>
  isLatestTradingDay(getCurrentDate()) &&
  getCurrentDate().hour() < LAST_TRACE_UPDATE_HOUR_ET;
